import { useState, type MouseEvent } from "react";
import type { OverviewData, ReviewItem, AiAgentSettingsData } from "../types";
import { originalReviewText } from "../review-text";
import { reviewMatchesFilter } from "../review-filters";
import { ReviewBoostSetupGuide, type ReviewBoostSetupReadiness } from "./ReviewBoostSetupGuide";
import { PlatformApiError, type PlatformApiClient } from "../../api";
import { reviewBoostV3GoogleState } from "../../review-boost-v3-readiness";
import { REVIEWBOOST_V4_UI_ENABLED } from "../../review-boost-v4-ui";
import { reviewBoostV4Priority } from "../../review-boost-v4-priority";

type PauseState = "idle" | "pending" | "uncertain";

/** A real, API-backed rendering of the approved V3 home. No mock state or provider writes on mount. */
export function OverviewV3View({
  data, reviews, aiAgentSettings, googleReadiness, base, navigate, slug, api, onExpired, reload,
}: {
  data: OverviewData;
  reviews: ReviewItem[];
  aiAgentSettings?: AiAgentSettingsData;
  googleReadiness?: ReviewBoostSetupReadiness;
  base: string;
  navigate: (path: string) => void;
  slug: string;
  api: PlatformApiClient;
  onExpired: () => void;
  reload: () => void;
}) {
  const [pauseState, setPauseState] = useState<PauseState>("idle");
  const [pauseMessage, setPauseMessage] = useState("");
  const mode = aiAgentSettings?.mode;
  const paused = mode === "paused";
  const pending = reviews.filter((item) => reviewMatchesFilter(item, "para_revisar")).length;
  const negative = reviews.filter((item) => reviewMatchesFilter(item, "negativas")).length;
  const unanswered = reviews.filter((item) => reviewMatchesFilter(item, "sin_responder")).length;
  const newest = reviews.slice().sort((a, b) => Date.parse(b.published_at) - Date.parse(a.published_at))[0];
  const displayName = data.org?.name?.trim() || data.locations?.[0]?.name || "tu negocio";
  const kpi = data.kpi;
  const googleState = reviewBoostV3GoogleState(googleReadiness);

  const priority = googleState === "unknown_connection"
    ? { label: "Revisemos la conexión con Google", detail: "No pudimos confirmar el estado de la autorización. Esto no significa que esté desconectada.", path: `${base}/settings/google`, action: "Ver conexión", count: "" }
    : googleState === "needs_connection"
      ? { label: "Revisá la conexión con Google", detail: "Conectá o renová la autorización desde Configuración para continuar.", path: `${base}/settings/google`, action: "Ver conexión", count: "" }
      : googleState === "unknown_locations"
        ? { label: "Comprobá las fichas de Google", detail: "La cuenta está conectada, pero todavía no pudimos confirmar sus ubicaciones. Revisá el estado sin asumir que están vacías.", path: `${base}/settings/google`, action: "Revisar fichas", count: "" }
        : googleState === "needs_location"
          ? { label: "Vinculá una ficha de Google", detail: "La cuenta está conectada, pero todavía no hay una ficha vinculada a un local de tu negocio.", path: `${base}/settings/google`, action: "Elegir ficha", count: "" }
          : REVIEWBOOST_V4_UI_ENABLED ? reviewBoostV4Priority(reviews, base) : negative > 0
        ? { label: `${negative} ${negative === 1 ? "reseña necesita" : "reseñas necesitan"} tu atención`, detail: `${negative} ${negative === 1 ? "reseña delicada" : "reseñas delicadas"} requieren atención. ${pending > 0 ? `La bandeja también tiene ${pending} ${pending === 1 ? "reseña pendiente" : "reseñas pendientes"} de revisión; ambos filtros pueden incluir una misma reseña.` : ""} La IA no publica casos sensibles sin la autorización correspondiente.`, path: `${base}/reviews?tab=negativas`, action: "Revisar ahora", count: "Necesita tu atención" }
        : pending > 0
          ? { label: `${pending} ${pending === 1 ? "reseña" : "reseñas"} para revisar`, detail: "Revisá los borradores y los casos que necesitan tu criterio antes de publicar.", path: `${base}/reviews?tab=para_revisar`, action: "Revisar respuestas", count: "Necesita tu atención" }
          : unanswered > 0
            ? { label: `${unanswered} ${unanswered === 1 ? "reseña sin" : "reseñas sin"} responder`, detail: "Podés preparar respuestas con IA y revisarlas antes de publicarlas.", path: `${base}/reviews?tab=sin_responder`, action: "Ir a reseñas", count: "Tu próxima acción" }
            : { label: "Tu bandeja está al día", detail: "No encontramos respuestas pendientes de tu revisión. Podés consultar los resultados o preparar la captación de nuevas reseñas.", path: `${base}/acquisition`, action: "Conseguir reseñas", count: "Todo al día" };

  const go = (path: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(path);
  };

  async function pauseAgent() {
    if (paused || pauseState === "pending") return;
    setPauseMessage("");
    setPauseState("pending");
    try {
      // The existing native authority performs the pause. Never optimistically mark it as paused.
      await api.reputation(slug, "/ai-agent/pause", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: "{}",
      });
      const verified = await api.reputation<{ settings: AiAgentSettingsData }>(slug, "/ai-agent/settings");
      if (verified.settings?.mode !== "paused") {
        setPauseState("uncertain");
        setPauseMessage("No pudimos confirmar la pausa. Revisá el estado antes de intentar otra vez.");
        return;
      }
      setPauseState("idle");
      setPauseMessage("La pausa fue confirmada por Review Boost.");
      reload();
    } catch (error) {
      if (error instanceof PlatformApiError && error.status === 401) {
        onExpired();
        return;
      }
      setPauseState("uncertain");
      setPauseMessage("No pudimos confirmar la pausa. Abrí Configuración para comprobar el estado antes de reintentar.");
    }
  }

  return <div className="rbv3-home" data-layout="review-boost-v3-real">
    <header className="rbv3-welcome">
      <div>
        <h1>Buen día, {displayName}.</h1>
        <p>Tu reputación, bajo control.</p>
      </div>
      {!REVIEWBOOST_V4_UI_ENABLED && <div className="rbv3-mode" aria-label="Control de la automatización">
        <span className="rbv3-mode-label">Modo: {mode === "safe_auto" ? "Automático protegido" : mode === "approval_required" ? "Con aprobación" : paused ? "Pausado" : "Sin confirmar"}</span>
        {paused ? <a href={`${base}/ai-agent`} onClick={go(`${base}/ai-agent`)}>Cambiar modo</a>
          : mode ? <><a href={`${base}/ai-agent`} onClick={go(`${base}/ai-agent`)}>Cambiar modo</a><button type="button" disabled={pauseState !== "idle"} onClick={() => void pauseAgent()}>{pauseState === "pending" ? "Pausando…" : "Pausar"}</button></>
            : <a href={`${base}/ai-agent`} onClick={go(`${base}/ai-agent`)}>Ver modo</a>}
      </div>}
    </header>
    {!REVIEWBOOST_V4_UI_ENABLED && mode && !paused && <p className="rbv3-mode-description">{mode === "approval_required" ? "La IA prepara borradores. Vos revisás y confirmás cada publicación." : "La IA puede responder los casos permitidos por tu configuración. Los casos sensibles necesitan revisión."}</p>}
    {pauseMessage && <p className={pauseState === "uncertain" ? "rbv3-alert rbv3-alert-warning" : "rbv3-alert"} role="status">{pauseMessage}</p>}
    {paused && <p className="rbv3-alert" role="status">La automatización figura pausada. Para reanudarla, elegí un modo en Configuración.</p>}

    <section className="rbv3-hero" aria-labelledby="rbv3-priority-title">
      <div className="rbv3-hero-copy">
        <span className="rbv3-kicker">{priority.count || "Próximo paso"}</span>
        <h2 id="rbv3-priority-title">{priority.label}</h2>
        <p>{priority.detail}</p>
        <a className="rbv3-primary" href={priority.path} onClick={go(priority.path)}>{priority.action} <span aria-hidden="true">→</span></a>
      </div>
    </section>

    {REVIEWBOOST_V4_UI_ENABLED && <section className="rbv4-mode-card" aria-label="Control de la automatización">
      <span className="rbv3-kicker">Modo guardado</span>
      <h2>{mode === "safe_auto" ? "Automático excepto sensibles" : mode === "approval_required" ? "Con aprobación" : paused ? "Pausado" : "Sin confirmar"}</h2>
      <p>{mode === "approval_required" ? "Configurado para preparar borradores. Vos revisás y confirmás cada publicación." : paused ? "La automatización está configurada en pausa. Conservás las herramientas manuales." : mode === "safe_auto" ? "Puede responder únicamente si el servicio y tus permisos lo autorizan. La ejecución efectiva no está verificada aquí. Los casos sensibles respetan las reglas guardadas." : "No pudimos comprobar el modo guardado. Revisalo en Configuración."}</p>
      <a href={`${base}/settings/automation`} onClick={go(`${base}/settings/automation`)}>Cambiar modo →</a>
    </section>}

    <ReviewBoostSetupGuide readiness={googleReadiness} reviewsAvailable={reviews.length > 0} base={base} navigate={navigate} includeAutomation={REVIEWBOOST_V4_UI_ENABLED} automationMode={mode} />

    <section className="rbv3-metrics" aria-label="Resumen de reseñas reales">
      <div><span>Reseñas</span><strong>{Number.isSafeInteger(kpi?.review_count) ? kpi.review_count : "—"}</strong><small>Registradas en Google</small></div>
      {REVIEWBOOST_V4_UI_ENABLED ? <div><span>Valoración promedio</span><strong>{typeof kpi?.rating === "number" ? `${kpi.rating.toLocaleString("es-AR")} / 5` : "—"}</strong><small>Según el resumen del servicio</small></div> : <div><span>Para revisar</span><strong>{pending}</strong><small>Borradores y casos sensibles</small></div>}
      <div><span>Respondidas este mes</span><strong>{Number.isSafeInteger(kpi?.responded_month) ? kpi.responded_month : "—"}</strong><small>Según el resumen del servicio</small></div>
    </section>

    <div className="rbv3-bottom-grid">
      <section className="rbv3-activity" aria-labelledby="rbv3-activity-title">
        <div className="rbv3-section-heading"><h2 id="rbv3-activity-title">Actividad reciente</h2><a href={`${base}/reviews`} onClick={go(`${base}/reviews`)}>Ver todas →</a></div>
        <div className="rbv3-activity-body">
          {REVIEWBOOST_V4_UI_ENABLED && newest ? reviews.slice().sort((a, b) => Date.parse(b.published_at) - Date.parse(a.published_at)).slice(0, 3).map(review => <a className="rbv4-activity-row" key={review.id} href={`${base}/reviews/${encodeURIComponent(review.id)}`} onClick={go(`${base}/reviews/${encodeURIComponent(review.id)}`)}><span className="rbv4-avatar" aria-hidden="true">{(review.author || "C").slice(0, 1)}</span><span><strong>{review.author || "Cliente"}</strong><small>{(originalReviewText(review.text) || "Sin texto escrito").slice(0, 140)}</small></span><span aria-label={`${review.rating} estrellas`}>{review.rating} ★</span><span aria-hidden="true">→</span></a>) : newest ? <><div className="rbv3-activity-row"><strong>Última reseña: {newest.author || "Cliente"}</strong><span>{(originalReviewText(newest.text) || "Sin texto escrito").slice(0, 128)}</span></div><a href={`${base}/reviews/${encodeURIComponent(newest.id)}`} onClick={go(`${base}/reviews/${encodeURIComponent(newest.id)}`)}>Abrir reseña →</a></>
            : <p>Cuando lleguen reseñas, vas a encontrar la actividad acá.</p>}
        </div>
      </section>
      <section className="rbv3-shortcuts" aria-label="Accesos rápidos">
        <h2>Accesos rápidos</h2>
        <a href={`${base}/acquisition`} onClick={go(`${base}/acquisition`)}><strong>Conseguir reseñas</strong><small>Preparación de campañas, sin envíos</small></a>
        <a href={`${base}/ai-agent`} onClick={go(`${base}/ai-agent`)}><strong>Cómo responde la IA</strong><small>Modo, voz y casos sensibles</small></a>
        <a href={`${base}/reports`} onClick={go(`${base}/reports`)}><strong>Ver resultados</strong><small>Reseñas y actividad verificable</small></a>
      </section>
    </div>
  </div>;
}
